import { useEffect, useRef } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { toast } from 'sonner';
import { useIsAdmin } from './useIsAdmin';

interface ProtectedAdminRouteProps {
  readonly children?: React.ReactNode;
}

/**
 * Route guard that only allows administrators through.
 *
 * - While the user-context query is loading, renders a centered spinner.
 * - Non-admins are redirected to `/` and shown a `sonner` error toast.
 * - Admins see `children` if provided, otherwise `<Outlet />` (so it can be used as
 *   either a wrapper or a layout-route `element`).
 *
 * Fails closed: if the role is unknown/undefined, access is denied.
 */
export function ProtectedAdminRoute({ children }: ProtectedAdminRouteProps) {
  const { isAdmin, isLoading } = useIsAdmin();
  const toastShownRef = useRef(false);

  // Fire the toast exactly once when we know the user is not admin.
  useEffect(() => {
    if (!isLoading && !isAdmin && !toastShownRef.current) {
      toastShownRef.current = true;
      toast.error('Administrator access required');
    }
  }, [isLoading, isAdmin]);

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] w-full items-center justify-center">
        <output
          aria-label="Loading"
          className="block h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground"
        />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children ?? <Outlet />}</>;
}
